/**
 * The Inbox, kept by each person — `docs/activity.md` §7.10 (Aaron, Oct 24,
 * notes 2.1–2.4): a right-click on a row (the context-menu key or Shift+F10
 * on one that has the focus, a long press on a phone) offers **Mark unread**
 * or **Mark read**, **Pin to top** or **Unpin**, **Archive** or **Move out of
 * archive**, and the person's own **categories** — ticked on a row, made with
 * **New category…**, renamed and removed in **Edit categories…**.
 *
 * Read and unread are offered on every row: on the viewer's own
 * conversations and Unassigned ones they are the viewer's, and on a
 * teammate's — a row is only ever in the list because the viewer may see that
 * teammate's Inbox — they are the teammate's (Aaron, Oct 25, answer 2.3), the
 * unread a row shows being its owner's (§7.1). The rest are the viewer's on
 * any row they can see. A category is a name and one of the ten mark colours,
 * drawn with `markInk`/`markTint` so no colour here is typed.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { type ConversationRow, type InboxCategory } from '@rowted/shared'
import { ContextMenu, type MenuItem } from '../../components/context-menu.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { BackLayer } from '../use-activity-history.js'
import { markInk } from '../../frame/mark-glyphs.js'
import { activityRequest } from '../api.js'
import { CategoriesEditor, CategoryChip, CategoryDialog, CategoryFilter, type CategorySet } from '../categories.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { DoneToast } from '../tasks/done-toast.js'
import type { ConversationsHandle } from './use-inbox.js'
import { errorWords } from './look.js'

// ---------------------------------------------------------------------------
// The furniture — `../categories.tsx`, shared with the tasks' categories
// (Aaron, Oct 25, note 1.5: "let's use the same thing"), with the Inbox's words
// ---------------------------------------------------------------------------

/** The Inbox's set: each person's own (§7.10). */
export const INBOX_CATEGORIES: CategorySet = {
  endpoint: '/conversations/categories',
  dialogSub: 'Only you see your categories.',
  editorTitle: 'Your categories',
  editorSub: 'Only you see them; a conversation can wear several.',
  placeholder: 'Waiting on HOA',
}

export { CategoryChip, CategoryFilter }

// ---------------------------------------------------------------------------
// What a row's menu does
// ---------------------------------------------------------------------------

export interface MarksOptions {
  list: ConversationsHandle
  reloadBadge: () => void
  /** The open conversation: archiving it lets the next one open. */
  selected: string | null
  onLeft: (id: string) => void
  /** Marked unread: the tab stops treating it as read on leaving it. */
  onMarkedUnread: (id: string) => void
  /** The list is showing the Archived view. */
  archivedView: boolean
}

interface Menu { row: ConversationRow; x: number; y: number; keyboard: boolean; from: HTMLElement | null }

/**
 * One owner of the menu, the phone's sheet, the two category dialogs and the
 * archive toast — rendered once by the tab; rows call `openMenu`/`openSheet`.
 */
export function useInboxMarks(options: MarksOptions) {
  const { list, reloadBadge, onLeft, onMarkedUnread } = options
  const [menu, setMenu] = useState<Menu | null>(null)
  const [sheet, setSheet] = useState<ConversationRow | null>(null)
  const [naming, setNaming] = useState<{ tag: ConversationRow | null } | null>(null)
  const [editing, setEditing] = useState(false)
  const [toast, setToast] = useState<{ title: string; undo: () => void } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const live = useRef(options)
  live.current = options

  const bump = (by: number) => {
    const counts = live.current.list.counts
    if (counts) live.current.list.setCounts({ ...counts, unread: Math.max(0, counts.unread + by) })
  }

  const run = useCallback(async (work: () => Promise<void>) => {
    setError(null)
    try {
      await work()
    } catch (cause) {
      setError(errorWords(cause))
      live.current.list.reload()
    }
  }, [])

  const setUnread = (row: ConversationRow, unread: boolean) => run(async () => {
    /* `mark`: the menu's Mark read, which on a teammate's is read for them — opening it is not (§7.1, 2.3). */
    await activityRequest(`/conversations/${row.id}/${unread ? 'unread' : 'read'}`,
      { method: 'POST', body: unread ? {} : { mark: true } })
    list.patch(row.id, { unread })
    if (row.unread !== unread && !row.archived) bump(unread ? 1 : -1)
    if (unread) onMarkedUnread(row.id)
    reloadBadge()
  })

  const setPinned = (row: ConversationRow, pinned: boolean) => run(async () => {
    await activityRequest(`/conversations/${row.id}/pin`, { method: 'POST', body: { pinned } })
    list.patch(row.id, { pinned })
    list.reload()
  })

  const setArchived = (row: ConversationRow, archived: boolean, undoable = true): Promise<void> => run(async () => {
    await activityRequest(`/conversations/${row.id}/archive`, { method: 'POST', body: { archived } })
    /* Archived leaves Unread and All; out of the archive leaves Archived. */
    if (archived !== live.current.archivedView) {
      list.drop(row.id)
      if (row.unread) bump(archived ? -1 : 1)
      if (live.current.selected === row.id) onLeft(row.id)
    } else {
      list.patch(row.id, { archived, pinned: archived ? false : row.pinned })
    }
    list.reload()
    reloadBadge()
    if (undoable) {
      setToast({
        title: archived ? `Archived · ${row.name}` : `Moved out of archive · ${row.name}`,
        undo: () => { setToast(null); void setArchived({ ...row, archived }, !archived, false) },
      })
    }
  })

  const setCategories = (row: ConversationRow, categoryIds: string[]) => run(async () => {
    await activityRequest(`/conversations/${row.id}/categories`, { method: 'PUT', body: { categoryIds } })
    const all = live.current.list.categories
    list.patch(row.id, { categories: all.filter((category) => categoryIds.includes(category.id)) })
  })

  const toggleCategory = (row: ConversationRow, category: InboxCategory) => {
    const has = row.categories.some((one) => one.id === category.id)
    const ids = has ? row.categories.filter((one) => one.id !== category.id).map((one) => one.id)
      : [...row.categories.map((one) => one.id), category.id]
    void setCategories(row, ids)
  }

  /** What the menu and the sheet both offer, in the same order. */
  const actions = (row: ConversationRow): { key: string; label: string; glyph: GlyphName; run: () => void }[] => [
    row.unread
      ? { key: 'read', label: 'Mark read', glyph: 'envelope', run: () => void setUnread(row, false) }
      : { key: 'unread', label: 'Mark unread', glyph: 'envelope', run: () => void setUnread(row, true) },
    ...(row.archived ? [] : [row.pinned
      ? { key: 'unpin', label: 'Unpin', glyph: 'pushpin' as const, run: () => void setPinned(row, false) }
      : { key: 'pin', label: 'Pin to top', glyph: 'pushpin' as const, run: () => void setPinned(row, true) }]),
    row.archived
      ? { key: 'unarchive', label: 'Move out of archive', glyph: 'archive', run: () => void setArchived(row, false) }
      : { key: 'archive', label: 'Archive', glyph: 'archive', run: () => void setArchived(row, true) },
  ]

  const menuItems = (row: ConversationRow): MenuItem[] => {
    const categories = list.categories
    return [
      ...actions(row).map((action) => ({ key: action.key, label: action.label, onSelect: action.run })),
      ...categories.map((category, index) => ({
        key: `category:${category.id}`, label: category.name, separatorBefore: index === 0,
        checked: row.categories.some((one) => one.id === category.id), swatch: markInk(category.colour),
        onSelect: () => toggleCategory(row, category),
      })),
      { key: 'new-category', label: 'New category…', separatorBefore: categories.length === 0,
        onSelect: () => setNaming({ tag: row }) },
      ...(categories.length > 0
        ? [{ key: 'edit-categories', label: 'Edit categories…', onSelect: () => setEditing(true) }] : []),
    ]
  }

  const openMenu = useCallback((row: ConversationRow, point: { x: number; y: number }, keyboard: boolean,
    from: HTMLElement | null) => {
    setMenu({ row, x: point.x, y: point.y, keyboard, from })
  }, [])
  const openSheet = useCallback((row: ConversationRow) => setSheet(row), [])

  /* The row in the sheet follows the list, so a tick shows at once. */
  const sheetRow = sheet ? list.rows.find((row) => row.id === sheet.id) ?? sheet : null
  /* A menu opened from the keyboard gives the focus back to its row; a pointer's goes wherever the pointer went. */
  const openMenuNow = useRef<Menu | null>(null)
  openMenuNow.current = menu
  const closeMenu = useCallback(() => {
    const current = openMenuNow.current
    setMenu(null)
    if (current?.keyboard) current.from?.focus()
  }, [])

  const categoriesChanged = () => {
    void activityRequest<{ items: InboxCategory[] }>('/conversations/categories')
      .then((answer) => list.setCategories(answer.items))
      .catch(() => undefined)
    list.reload()
  }

  useEffect(() => {
    if (!error) return
    const timer = window.setTimeout(() => setError(null), 6000)
    return () => window.clearTimeout(timer)
  }, [error])

  const element = (
    <>
      {menu && (
        <ContextMenu items={menuItems(list.rows.find((row) => row.id === menu.row.id) ?? menu.row)}
          x={menu.x} y={menu.y} onClose={closeMenu} focusFirst={menu.keyboard} label={`${menu.row.name} — actions`} />
      )}
      {/* A phone's sheet: back closes it (Aaron, Oct 26). */}
      {sheetRow && <BackLayer onBack={() => setSheet(null)} />}
      {sheetRow && (
        <PanelSheet open onClose={() => setSheet(null)} title={sheetRow.name} layer={70}>
          {actions(sheetRow).map((action) => (
            <SheetRow key={action.key} glyph={action.glyph} label={action.label}
              onClick={() => { setSheet(null); action.run() }} />
          ))}
          <div className="px-4 pb-1.5 pt-4 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
            Categories
          </div>
          {list.categories.map((category) => {
            const on = sheetRow.categories.some((one) => one.id === category.id)
            return (
              <button key={category.id} type="button" role="menuitemcheckbox" aria-checked={on}
                onClick={() => toggleCategory(sheetRow, category)}
                className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left text-[15px] font-medium">
                <i aria-hidden className="block h-3 w-3 shrink-0 rounded-full" style={{ background: markInk(category.colour) }} />
                <span className="flex-1 truncate">{category.name}</span>
                {on && <span className="inline-flex text-accent"><Glyph name="check" size={18} /></span>}
              </button>
            )
          })}
          <SheetRow glyph="plus" label="New category…" onClick={() => { setSheet(null); setNaming({ tag: sheetRow }) }} />
          {list.categories.length > 0 && (
            <SheetRow glyph="pencil" label="Edit categories…" onClick={() => { setSheet(null); setEditing(true) }} />
          )}
        </PanelSheet>
      )}
      {naming && (
        <CategoryDialog set={INBOX_CATEGORIES} category={null} onClose={() => setNaming(null)}
          onSaved={(made) => {
            list.setCategories([...list.categories, made])
            const row = naming.tag ? list.rows.find((one) => one.id === naming.tag!.id) ?? naming.tag : null
            if (row) {
              void activityRequest(`/conversations/${row.id}/categories`, {
                method: 'PUT', body: { categoryIds: [...row.categories.map((one) => one.id), made.id] },
              }).then(() => list.patch(row.id, { categories: [...row.categories, made] }))
                .catch((cause: unknown) => setError(errorWords(cause)))
            }
          }} />
      )}
      {editing && <CategoriesEditor set={INBOX_CATEGORIES} categories={list.categories} onClose={() => setEditing(false)} onChanged={categoriesChanged} />}
      {toast && <DoneToast title={toast.title} sub={null} onUndo={toast.undo} onDismiss={() => setToast(null)} />}
      {error && (
        <div role="alert" className="fixed bottom-[calc(24px+var(--activity-foot,0px))] left-1/2 z-[70] -translate-x-1/2 rounded-xl border border-danger-ring bg-danger-bg px-4 py-2.5 text-[13px] text-danger">
          {error}
        </div>
      )}
    </>
  )

  return { openMenu, openSheet, editCategories: () => setEditing(true), element }
}

function SheetRow({ glyph, label, onClick }: { glyph: GlyphName; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left text-[15px] font-medium">
      <span className="inline-flex text-text-muted"><Glyph name={glyph} size={18} /></span>{label}
    </button>
  )
}

/**
 * Where a menu opened from the keyboard sits — beside the row, since there
 * is no pointer to open at.
 */
export function menuPointFor(element: HTMLElement): { x: number; y: number } {
  const rect = element.getBoundingClientRect()
  return { x: rect.left + 48, y: rect.top + Math.min(rect.height, 40) }
}
